<template>
  <div v-if="isOpen" ref="popupEl" class="trip360" :class="{ 'is-collapsed': isCollapsed }" :style="popupStyle">
    <div ref="handleEl" class="t360-head" :class="{ 'is-dragging': isDragging }">
      <div class="t360-head-info">
        <span class="t360-key">{{ key }}</span>
        <span
          v-if="trip?.status_name"
          class="t360-status-pill"
          :class="trip360StatusClass(trip.status_name)"
        >{{ trip.status_name }}</span>
        <span v-if="trip?.uid" class="t360-uid" title="UID — связующий ключ карточки">{{ trip.uid }}</span>
        <span v-if="trip?.display_name" class="t360-name">{{ trip.display_name }}</span>
      </div>
      <div class="t360-head-actions" @pointerdown.stop>
        <button
          v-if="mode === MODE_FULL"
          type="button"
          class="t360-icon-btn"
          title="Компактный режим"
          @click="setModeOverride(MODE_COMPACT)"
        >Б</button>
        <button
          v-else
          type="button"
          class="t360-icon-btn"
          title="Полный режим"
          @click="setModeOverride(MODE_FULL)"
        >П</button>
        <button
          type="button"
          class="t360-icon-btn"
          title="Открыть в новом окне (текущее окно 360 закроется)"
          @click="openNewWindow"
        >⧉</button>
        <a
          class="t360-icon-btn t360-link-btn"
          :href="pageUrl"
          target="_blank"
          rel="noopener"
          title="Открыть в новой вкладке (текущее окно 360 закроется)"
          @click="onNewTabClick"
        >↗</a>
        <button v-if="canEdit" type="button" class="t360-edit-btn" @click="onEditClick">
          Редактировать
        </button>
        <button
          type="button"
          class="t360-icon-btn"
          :title="isCollapsed ? 'Развернуть окно' : 'Свернуть до заголовка'"
          @click="toggleCollapse"
        >{{ isCollapsed ? '▸' : '▾' }}</button>
        <button type="button" class="t360-icon-btn t360-close" title="Закрыть (Esc)" @click="close">×</button>
      </div>
    </div>

    <div v-show="!isCollapsed" class="t360-body">
      <Trip360Content :trip="trip" :loading="loading" :error="error" :mode="mode" :initial-tab="initialTab" />
    </div>

    <template v-if="!isCollapsed">
      <div class="t360-rz t360-rz-n" title="Растянуть по высоте" @pointerdown="onResizeStart($event, 'n')"></div>
      <div class="t360-rz t360-rz-s" title="Растянуть по высоте" @pointerdown="onResizeStart($event, 's')"></div>
      <div class="t360-rz t360-rz-w" title="Растянуть по ширине" @pointerdown="onResizeStart($event, 'w')"></div>
      <div class="t360-rz t360-rz-e" title="Растянуть по ширине" @pointerdown="onResizeStart($event, 'e')"></div>
      <div class="t360-resize" title="Изменить размер" @pointerdown="onResizeStart($event, 'se')"></div>
    </template>
  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue'
import { useStore } from 'vuex'
import { useDraggable, useElementSize } from '@vueuse/core'
import { createToaster } from '@meforma/vue-toaster'
import { useTrip360Store } from '@/stores/trip360'
import Trip360Content from '@/components/trip360/Trip360Content.vue'
import {
  MODE_COMPACT,
  MODE_FULL,
  DEFAULT_HEIGHT,
  DEFAULT_WIDTH,
  resolveMode,
  normalizeOverrideOnResize,
  clampPosition,
  clampSize,
  defaultPosition,
  loadTrip360Geometry,
  saveTrip360Geometry,
  trip360PageUrl,
  trip360WindowFeatures,
  trip360StatusClass,
  centeredOverViewport,
} from '@/components/trip360/trip360Logic.mjs'

// Высота свёрнутого окна (только шапка) — для клампа позиции.
const COLLAPSED_HEIGHT = 60

export default {
  name: 'Trip360Popup',
  components: { Trip360Content },
  setup() {
    const store = useStore()
    const trip360 = useTrip360Store()
    const toaster = createToaster()

    const popupEl = ref(null)
    const handleEl = ref(null)

    const viewport = () => ({ width: window.innerWidth, height: window.innerHeight })

    // Геометрия окна (размер/позиция/свёрнутость) переживает перезагрузку.
    const savedGeometry = loadTrip360Geometry(viewport())

    const width = ref(savedGeometry ? savedGeometry.width : DEFAULT_WIDTH)
    const height = ref(savedGeometry ? savedGeometry.height : DEFAULT_HEIGHT)
    const isCollapsed = ref(!!(savedGeometry && savedGeometry.collapsed))

    // Свёрнутое окно занимает только шапку — клампим позицию по его фактической высоте.
    const effHeight = () => (isCollapsed.value ? COLLAPSED_HEIGHT : height.value)

    function persistGeometry() {
      saveTrip360Geometry({
        x: x.value,
        y: y.value,
        width: width.value,
        height: height.value,
        collapsed: isCollapsed.value,
      })
    }

    function toggleCollapse() {
      isCollapsed.value = !isCollapsed.value
      const pos = clampPosition(x.value, y.value, width.value, effHeight(), viewport())
      x.value = pos.x
      y.value = pos.y
      persistGeometry()
    }

    const startPos = savedGeometry || defaultPosition(viewport())
    const { x, y, isDragging } = useDraggable(popupEl, {
      handle: handleEl,
      initialValue: { x: startPos.x, y: startPos.y },
      onMove(pos) {
        const clamped = clampPosition(pos.x, pos.y, width.value, effHeight(), viewport())
        pos.x = clamped.x
        pos.y = clamped.y
      },
      onEnd: persistGeometry,
    })

    // При сжатии вьюпорта окно остаётся целиком на экране.
    const onWindowResize = () => {
      const vp = viewport()
      const size = clampSize(width.value, height.value, vp)
      width.value = size.width
      height.value = size.height
      const pos = clampPosition(x.value, y.value, size.width, effHeight(), vp)
      x.value = pos.x
      y.value = pos.y
      persistGeometry()
    }
    onMounted(() => window.addEventListener('resize', onWindowResize))
    onBeforeUnmount(() => window.removeEventListener('resize', onWindowResize))

    // Ресайз за любой край/угол: dir из символов n/s/w/e.
    // За западный/северный край противоположный край остаётся на месте —
    // двигаем позицию окна вместе с размером.
    function onResizeStart(event, dir = 'se') {
      if (event.button !== 0 || isCollapsed.value) return
      event.preventDefault()
      event.stopPropagation()
      const startX = event.clientX
      const startY = event.clientY
      const startW = width.value
      const startH = height.value
      const startLeft = x.value
      const startTop = y.value
      const east = dir.includes('e')
      const south = dir.includes('s')
      const west = dir.includes('w')
      const north = dir.includes('n')
      const onMove = (ev) => {
        const dx = ev.clientX - startX
        const dy = ev.clientY - startY
        const size = clampSize(
          startW + (east ? dx : 0) - (west ? dx : 0),
          startH + (south ? dy : 0) - (north ? dy : 0),
          viewport(),
        )
        width.value = size.width
        height.value = size.height
        const pos = clampPosition(
          west ? startLeft + (startW - size.width) : x.value,
          north ? startTop + (startH - size.height) : y.value,
          size.width,
          size.height,
          viewport(),
        )
        x.value = pos.x
        y.value = pos.y
      }
      const onUp = () => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp)
        persistGeometry()
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
    }

    const { width: elWidth } = useElementSize(popupEl)

    // Ручной оверрайд режима живёт до пересечения порога авто-режима.
    watch(elWidth, (next, prev) => {
      const prevW = prev === undefined ? next : prev
      const normalized = normalizeOverrideOnResize(prevW, next, trip360.modeOverride)
      if (normalized !== trip360.modeOverride) {
        trip360.setModeOverride(normalized)
      }
    })

    const mode = computed(() => resolveMode(elWidth.value, trip360.modeOverride))

    // Геометрия сохраняется между сессиями и при закрытии окна.
    watch(
      () => trip360.isOpen,
      (open) => {
        if (!open) persistGeometry()
      },
    )

    const popupStyle = computed(() => ({
      transform: `translate3d(${x.value}px, ${y.value}px, 0)`,
      width: `${width.value}px`,
      height: isCollapsed.value ? 'auto' : `${height.value}px`,
    }))

    const canEdit = computed(() => store.getters.hasPermission('trip360.edit'))

    function onEditClick() {
      toaster.info('Редактирование рейса в 360-окне пока не реализовано')
    }

    // Standalone-ссылка: /trip360/<ключ> — открытие в новой вкладке/окне.
    const pageUrl = computed(() =>
      trip360PageUrl(trip360.key, {
        origin: window.location.origin,
        base: process.env.BASE_URL,
      }),
    )

    // Каждый клик открывает отдельное окно (без переиспользования имени),
    // по центру вьюпорта родительского окна; попап на странице закрывается.
    function openNewWindow() {
      const url = pageUrl.value
      if (!url) return
      const size = {
        width: isCollapsed.value ? DEFAULT_WIDTH : width.value,
        height: isCollapsed.value ? DEFAULT_HEIGHT : height.value,
      }
      const pos = centeredOverViewport({
        ...size,
        viewportX: window.screenX,
        viewportY: window.screenY,
        viewportWidth: window.outerWidth,
        viewportHeight: window.outerHeight,
      })
      const win = window.open(url, '_blank', trip360WindowFeatures({ ...size, x: pos.x, y: pos.y }))
      if (win) {
        trip360.close()
      } else {
        toaster.error('Браузер заблокировал всплывающее окно — разрешите pop-up для сайта')
      }
    }

    // Новая вкладка: навигация по ссылке + закрыть попап на странице.
    function onNewTabClick() {
      trip360.close()
    }

    function onKeydown(event) {
      if (event.key === 'Escape' && trip360.isOpen) trip360.close()
    }
    onMounted(() => window.addEventListener('keydown', onKeydown))
    onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))

    return {
      MODE_COMPACT,
      MODE_FULL,
      popupEl,
      handleEl,
      isDragging,
      popupStyle,
      mode,
      canEdit,
      isOpen: computed(() => trip360.isOpen),
      key: computed(() => trip360.key),
      trip: computed(() => trip360.trip),
      loading: computed(() => trip360.loading),
      error: computed(() => trip360.error),
      initialTab: computed(() => trip360.initialTab),
      close: trip360.close,
      setModeOverride: trip360.setModeOverride,
      onResizeStart,
      isCollapsed,
      toggleCollapse,
      onEditClick,
      pageUrl,
      openNewWindow,
      onNewTabClick,
      trip360StatusClass,
    }
  },
}
</script>

<style scoped>
.trip360 {
  position: fixed;
  left: 0;
  top: 0;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #d0d5dd;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(16, 24, 40, 0.18);
  overflow: hidden;
}

.t360-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  cursor: grab;
  user-select: none;
  touch-action: none;
}

.t360-head.is-dragging {
  cursor: grabbing;
}

.trip360.is-collapsed .t360-head {
  border-bottom: none;
}

.t360-head-info {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}

.t360-key {
  font-weight: 700;
  font-size: 14px;
  color: #101828;
  white-space: nowrap;
}

.t360-uid {
  font-size: 11px;
  font-weight: 600;
  color: #5925dc;
  background: #f4f0ff;
  border-radius: 8px;
  padding: 2px 8px;
  white-space: nowrap;
}

.t360-name {
  font-size: 12px;
  color: #667085;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.t360-head-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.t360-icon-btn {
  min-width: 26px;
  height: 26px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #475467;
  font-size: 13px;
  line-height: 1;
  cursor: pointer;
  padding: 0 6px;
}

.t360-icon-btn:hover {
  background: #f2f4f7;
}

.t360-link-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.t360-close {
  font-size: 16px;
}

.t360-edit-btn {
  height: 26px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0 10px;
}

.t360-edit-btn:hover {
  background: #eff6ff;
}

.t360-body {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 10px;
}

.t360-resize {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 16px;
  height: 16px;
  cursor: nwse-resize;
  touch-action: none;
  background: linear-gradient(135deg, transparent 50%, #c7cdd6 50%);
}

/* Боковые ручки растягивания (верх/низ/лево/право). */
.t360-rz {
  position: absolute;
  z-index: 5;
  touch-action: none;
}

.t360-rz-n {
  top: -2px;
  left: 10px;
  right: 10px;
  height: 6px;
  cursor: ns-resize;
}

.t360-rz-s {
  bottom: -2px;
  left: 10px;
  right: 10px;
  height: 6px;
  cursor: ns-resize;
}

.t360-rz-w {
  left: -2px;
  top: 10px;
  bottom: 10px;
  width: 6px;
  cursor: ew-resize;
}

.t360-rz-e {
  right: -2px;
  top: 10px;
  bottom: 10px;
  width: 6px;
  cursor: ew-resize;
}
</style>
